import { Controller } from "@hotwired/stimulus"
import { primeModalFrames, refreshModalFrames, resetModalFrames } from "../lib/modal_frames"

// FD-566: a native <dialog> opened with showModal() lives in the browser's top layer and
// makes the rest of the document inert. Turbo's morph refresh (turbo_refreshes_with
// :morph — same-url form redirects AND FD-238 broadcast refreshes) only strips the
// dialog's `open` attribute while morphing; it never runs the dialog close algorithm, so
// morphing an OPEN modal leaves it in the top layer with the page inert (frozen). Skip
// morphing any open <dialog> so its live top-layer state is preserved (same effect as
// data-turbo-permanent, but only while it's open — a closed dialog morphs normally).
function preserveOpenDialog(event) {
  const el = event.target
  if (el instanceof HTMLDialogElement && el.open) event.preventDefault()
}

// Cornerstone spike: a <cs-dialog> is the other shape. Its native <dialog> sits in its shadow
// root, which a morph never touches, and the host reflects its state as an `open` attribute
// the server never renders. So a morph does not freeze the page here; it removes `open`,
// which closes the dialog properly. That still shuts a dialog on the same-url redirect its
// own form causes (or on a broadcast refresh), so keep just that attribute. Everything else
// on the dialog and its content still morphs, so a list inside it shows the write.
function keepCsDialogOpen(event) {
  const { attributeName, mutationType } = event.detail || {}
  if (event.target.localName === "cs-dialog" && attributeName === "open" && mutationType === "remove") {
    event.preventDefault()
  }
}

// FD-603: …and because the morph above skips the dialog, the detail modal kept rendering
// pre-save values after its own inline edits (the write landed; only the UI was stale).
// Re-fetch the dialog's detail frame instead — the same thing a close + re-open does.
//
// Narrow on purpose. Three guards, each earning its place:
//
//   success        — nothing to show for a request that never happened.
//   redirected     — the ONLY responses we want are the PATCH → 302 redirect_back ones
//                    that trigger the skipped morph. turbo:submit-end fires BEFORE a
//                    turbo_stream response is applied, so refreshing on those would wipe
//                    the very element the stream is about to target: the comment composer
//                    (turbo_stream.append "activity-feed"), comment delete, and every
//                    reaction toggle all answer with streams from inside this modal.
//   not frozen     — data-turbo-permanent is how inline_edit freezes an open Tiptap edit
//                    (FD-239). Idiomorph honours it; a turbo-FRAME render does not (its
//                    permanent map is built from the SERVER's fragment, which never
//                    carries the attribute), so replacing the frame would silently throw
//                    away unsaved text. inline-edit#save drops the freeze before its own
//                    submit, so saving the description still refreshes.
function refreshOpenDialogAfterWrite(event) {
  if (!event.detail?.success) return
  if (!event.detail?.fetchResponse?.redirected) return
  const dialog = event.target.closest?.("dialog")
  if (!dialog?.open) return
  if (dialog.querySelector("[data-turbo-permanent]")) return
  refreshModalFrames(dialog)
}

// Both listeners are document-level and stateless, but `data-controller="modal"` is
// mounted more than once per page (<body>, plus the shortcuts modal's Close button), so
// register them for the FIRST instance only — otherwise every write fires the handler
// twice, and the second pass cancels the frame fetch the first one just started.
let liveControllers = 0

// Opens/closes native <dialog> modals by id
// (data-action="modal#open" data-modal-id-param="new-item-modal").
export default class extends Controller {
  connect() {
    if (liveControllers++ > 0) return
    document.addEventListener("turbo:before-morph-element", preserveOpenDialog)
    document.addEventListener("turbo:before-morph-attribute", keepCsDialogOpen)
    document.addEventListener("turbo:submit-end", refreshOpenDialogAfterWrite)
  }

  disconnect() {
    liveControllers = Math.max(0, liveControllers - 1)
    if (liveControllers > 0) return
    document.removeEventListener("turbo:before-morph-element", preserveOpenDialog)
    document.removeEventListener("turbo:before-morph-attribute", keepCsDialogOpen)
    document.removeEventListener("turbo:submit-end", refreshOpenDialogAfterWrite)
  }

  open(event) {
    event.preventDefault()
    const id = event.params.id
    const dialog = document.getElementById(id)
    if (!dialog) return
    // FD-568: the trigger can live inside an open dropdown (the row "…" quick-actions
    // menu). Close it before showing the modal, or it lingers behind the dialog — and
    // frame_reveal's morph guard would pin it open for as long as the modal is up.
    event.target.closest("details.dropdown")?.removeAttribute("open")
    // FD-98/FD-501/FD-568: fetch the dialog's deferred frames at open time — once for
    // static bodies, every open for a data-modal-dynamic one (per-record/per-group).
    const frame = primeModalFrames(dialog, event.params.src)
    if (frame) {
      // A dynamic body isn't in the DOM yet, so the focus below finds nothing — focus
      // once it arrives (the confirmation asks the user to type, so this matters).
      frame.addEventListener("turbo:frame-load", () => this.#focusFirstField(dialog), { once: true })
    }
    // FD-591: when this dialog closes, drop any loaded static body back to its
    // placeholder so a page rendering many detail modals (Drafts) never accumulates
    // duplicate _detail_body ids. The native `close` event covers every close path
    // (button, Escape, backdrop, closeOnSuccess).
    dialog.addEventListener("close", () => resetModalFrames(dialog), { once: true })
    dialog.showModal()
    this.#focusFirstField(dialog)
  }

  #focusFirstField(dialog) {
    dialog.querySelector("[autofocus], input[type=text], textarea")?.focus()
  }

  close(event) {
    event.target.closest("dialog")?.close()
  }

  // FD-566: close the modal <dialog> whose form just submitted successfully —
  // BEFORE Turbo's redirect morph render runs. Calling the real close algorithm
  // removes the dialog from the top layer, so the morph reconciles a closed dialog
  // and the page stays interactive. A validation failure (success === false)
  // re-renders the form, so we leave the dialog open. Mirrors suggestion_controller
  // (the FD-401 Suggest-an-improvement modal).
  closeOnSuccess(event) {
    if (!event.detail?.success) return
    const dialog = event.target.closest("dialog")
    if (!dialog) return
    event.target.reset?.()
    dialog.close()
  }
}
